/**
 * Nav Badges API Service
 *
 * Fetches and acknowledges nav badge state (red-dot notifications) for sidebar/tabs.
 * Uses appcore-api base URL (nav-badges lives there); same auth (Bearer token) as rest of app.
 *
 * @module services/api/NavBadgesApiService
 */

import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { getAppcoreApiBaseUrl } from '@/config/api';
import { CommunicationBridge } from '@sequelae/lore-mfe-kit';
import { SESSION_EXPIRED_EVENT } from '@/contexts/AuthContext';
import type {
  NavBadgesResponse,
  NavBadgeSection,
  NavBadgeAckResponse,
} from '@/types/navBadges';

const api: AxiosInstance = axios.create({
  baseURL: getAppcoreApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  async (config) => {
    let token = CommunicationBridge.getAccessToken();
    if (!token) {
      token =
        localStorage.getItem('gcip_access_token') ||
        localStorage.getItem('access_token') ||
        localStorage.getItem('mock_access_token') ||
        localStorage.getItem('token_paste_access_token') ||
        null;
      if (token) {
        CommunicationBridge.setAccessToken(token);
      }
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }
    return Promise.reject(error);
  }
);

const MAX_ACK_RETRIES = 2; // 1 initial + 2 retries = 3 total attempts
const ACK_RETRY_DELAY_MS = 800;

/**
 * Fetch current nav badge state for all sections.
 * @returns null if endpoint is not available (404) so caller can disable the feature.
 */
export async function getNavBadges(): Promise<NavBadgesResponse | null> {
  try {
    const response: AxiosResponse<NavBadgesResponse> = await api.get(
      '/api/v1/external/nav-badges/'
    );
    if (!response?.data) {
      return null;
    }
    return response.data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      return null;
    }
    throw err;
  }
}

/**
 * Acknowledge a section (user visited that screen). Retries on transient failures.
 * Does not retry on 404 (endpoint not deployed). Logs to console on failure; does not throw.
 */
export async function acknowledgeNavBadge(
  section: NavBadgeSection
): Promise<void> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= MAX_ACK_RETRIES; attempt++) {
    try {
      const response: AxiosResponse<NavBadgeAckResponse> = await api.post(
        `/api/v1/external/nav-badges/acknowledgments/${section}`,
        {}
      );
      if (response?.data?.success) {
        return;
      }
      lastError = new Error(response?.data?.message ?? 'Acknowledgment failed');
    } catch (err: unknown) {
      lastError = err;
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        return;
      }
      if (attempt < MAX_ACK_RETRIES) {
        console.warn(
          `[NavBadges] Acknowledge ${section} failed (attempt ${attempt + 1}/${MAX_ACK_RETRIES + 1}), retrying...`,
          err
        );
        await new Promise((r) => setTimeout(r, ACK_RETRY_DELAY_MS));
      }
    }
  }
  console.warn(
    `[NavBadges] Acknowledge ${section} failed after ${MAX_ACK_RETRIES + 1} attempts`,
    lastError
  );
}
